<template>
  <section class="register-container">
    <div class="register-card">
      <h1 class="form-title">{{ $t('register.title') }}</h1>

      <form class="register-form" @submit.prevent="register">
        <input v-model="name" type="text" :placeholder="$t('register.name')" required />
        <input v-model="surname" type="text" :placeholder="$t('register.surname')" required />
        <input
          v-model="honeypot"
          type="text"
          name="hp"
          autocomplete="off"
          tabindex="-1"
          aria-hidden="true"
          class="honeypot"
        />

        <div class="form-group">
          <label for="birthDate">{{ $t('register.birthDate') }}</label>
          <input
            id="birthDate"
            v-model="birthDate"
            type="date"
            @change="checkAge"
            required
          />
        </div>
        <p v-if="ageError" class="input-error">{{ $t('register.mustBeOver18') }}</p>

        <vue-tel-input
          v-model="phone"
          :defaultCountry="'TR'"
          :enableDropdown="true"
          :preferredCountries="['TR', 'US', 'DE']"
          :inputOptions="{
            nationalMode: true,
            showDialCode: false,
            autoInsertDialCode: true,
            autoFormat: true,
            placeholder: customPlaceholder
          }"
          @blur="checkPhone"
          @country-changed="onCountryChange"
        />
        <p v-if="phoneError" class="input-error">{{ phoneError }}</p>

        <input v-model="email" type="email" :placeholder="$t('register.email')" required />
        <p v-if="emailError" class="input-error">{{ emailError }}</p>

        <input v-model="password" type="password" :placeholder="$t('register.password')" required />
        <p v-if="password && !isValidPassword(password)" class="input-error">
          {{ $t('register.passwordRule') }}
        </p>

        <label class="checkbox">
        <input type="checkbox" v-model="agreedTerms" />
        <span>
          <a :href="termsLink" target="_blank">{{ $t('register.terms') }}</a> {{ $t('register.termsText') }}
        </span>
      </label>

      <label class="checkbox">
        <input type="checkbox" v-model="agreedPrivacy" />
        <span>
          <a :href="privacyLink" target="_blank">{{ $t('register.privacy') }}</a> {{ $t('register.privacyText') }}
        </span>
      </label>

       <button class="btn" :disabled="!agreedTerms || !agreedPrivacy">
        {{ $t('register.registerButton') }}
      </button>

        <p v-if="errorMessage" class="error">{{ errorMessage }}</p>

        <p class="login-link">
          {{ $t('register.alreadyHaveAccount') }}
          <router-link to="/login">{{ $t('register.goLogin') }}</router-link>
        </p>
      </form>
    </div>
  </section>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { parsePhoneNumberFromString } from 'libphonenumber-js'

const { locale, t } = useI18n()
const router = useRouter()
const baseURL = process.env.VUE_APP_API_URL
const honeypot = ref('')

const name = ref('')
const surname = ref('')
const email = ref('')
const phone = ref('')
const password = ref('')
const agreed = ref(false)
const birthDate = ref('')
const ageError = ref(false)

const errorMessage = ref('')
const phoneError = ref('')
const emailError = ref('')
const selectedCountry = ref('TR')
const customPlaceholder = ref('5xx xxx xx xx')
const agreedTerms = ref(false)
const agreedPrivacy = ref(false)

const termsLink = computed(() => `/documents/${locale.value}/kvkk.txt`)
const privacyLink = computed(() => `/documents/${locale.value}/privacy.txt`)

function onCountryChange(country) {
  selectedCountry.value = country.iso2
  customPlaceholder.value = country.iso2 === 'TR' ? '5xx xxx xx xx' : 'Phone number'
}

function isValidEmail(email) {
  const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
  return re.test(email)
}

function isValidPassword(pw) {
  const re = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/
  return re.test(pw)
}

function checkPhone() {
  const parsed = parsePhoneNumberFromString(phone.value, selectedCountry.value)
  if (!parsed || !parsed.isValid()) {
    phoneError.value = t('register.invalidPhone')
    return false
  }

  if (parsed.country === 'TR' && !/^5\d{9}$/.test(parsed.nationalNumber)) {
    phoneError.value = t('register.invalidPhone')
    return false
  }

  phoneError.value = ''
  return true
}

function checkAge() {
  const today = new Date()
  const bDate = new Date(birthDate.value)
  let age = today.getFullYear() - bDate.getFullYear()
  const m = today.getMonth() - bDate.getMonth()
  if (m < 0 || (m === 0 && today.getDate() < bDate.getDate())) {
    age--
  }
  ageError.value = age < 18
}

async function register() {
  emailError.value = ''
  errorMessage.value = ''

  if (!name.value || !surname.value || !email.value || !password.value || !phone.value) {
    errorMessage.value = t('register.fillAllFields')
    return
  }

  if (!isValidEmail(email.value)) {
    emailError.value = t('register.invalidEmail')
    return
  }

  if (!isValidPassword(password.value)) {
    errorMessage.value = t('register.passwordRule')
    return
  }

  if (ageError.value) {
    errorMessage.value = t('register.mustBeOver18')
    return
  }

  if (!checkPhone()) {
    errorMessage.value = t('register.invalidPhone')
    return
  }

  if (!agreedTerms.value || !agreedPrivacy.value) {
  errorMessage.value = t('register.mustAccept')
  return
  }

  const parsed = parsePhoneNumberFromString(phone.value, selectedCountry.value)
  const fullPhone = parsed?.number || ''

  const res = await fetch(`${baseURL}/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name: name.value,
      surname: surname.value,
      email: email.value,
      phone: fullPhone,
      password: password.value,
      role: 'user',
      birth_date: birthDate.value,
      hp: honeypot.value
    })
  })

  const data = await res.json()

  if (!res.ok) {
    if (data.message?.includes('e-posta')) {
      emailError.value = data.message
    } else {
      errorMessage.value = data.message || 'Kayıt başarısız.'
    }
    return
  }

  alert('Kayıt başarılı!')
  router.push('/login')
}
</script>
  
<style scoped>
  .form-title {
    text-align: center;
    font-size: 22px;
    margin-bottom: 20px;
  }
  .form-group {
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
  }
  .form-title span {
    font-size: 16px;
    font-weight: normal;
    color: #666;
  }
  label {
  font-size: 0.85rem;
  color: #555;
  margin-bottom: 4px;
  }
  input[type="date"] {
    padding: 10px;
    border: 1px solid #ccc;
    border-radius: 8px;
    font-size: 0.95rem;
  }
  .register-form {
    display: flex;
    flex-direction: column;
    gap: 15px;
    max-width: 400px;
    margin: 0 auto;
  }
  .register-form input {
    padding: 10px;
    border-radius: 8px;
    border: 1px solid #ccc;
  }
  .checkbox {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 0.9em;
  }
  .btn {
    background-color: #0c2d57;
    color: white;
    padding: 10px;
    border: none;
    border-radius: 8px;
    font-weight: bold;
    cursor: pointer;
  }
  .btn:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
  .btn:hover:enabled {
    background-color: #0a2444;
  }
  .login-link {
    text-align: center;
    margin-top: 20px;
  }
  .error {
    color: red;
    text-align: center;
    font-size: 0.9em;
  }
  .input-error {
    color: red;
    font-size: 0.85em;
    margin-top: -10px;
    margin-bottom: 10px;
  }
.register-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: calc(100vh - 120px); /* header+footer boşluğu */
  padding: 40px 20px 80px;
  box-sizing: border-box;
  background: transparent;
}
  .register-card {
    background: rgba(255, 255, 255, 0.9);
    padding: 32px;
    border-radius: 16px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
    width: 100%;
    max-width: 440px;
    margin-top: 40px;

  }
  .register-form {
    display: flex;
    flex-direction: column;
    gap: 15px;
  }

  .register-form input {
    padding: 10px;
    border-radius: 8px;
    border: 1px solid #ccc;
    font-size: 14px;
  }

  .form-title {
    text-align: center;
    font-size: 24px;
    font-weight: bold;
    margin-bottom: 20px;
  }

  .checkbox {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 0.9em;
  }

  .btn {
    background-color: #0c2d57;
    color: white;
    padding: 10px;
    border: none;
    border-radius: 8px;
    font-weight: bold;
    cursor: pointer;
    transition: background-color 0.3s ease;
  }
  .btn:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
  .btn:hover:enabled {
    background-color: #0a2444;
  }

  .login-link {
    text-align: center;
    margin-top: 10px;
    font-size: 14px;
  }
.honeypot {
  position: absolute !important;
  left: -9999px;
  opacity: 0;
  pointer-events: none;
}
  .error,
  .input-error {
    color: red;
    font-size: 13px;
    margin-top: -10px;
    margin-bottom: 5px;
    text-align: left;
  }
  @media (max-width: 600px) {
    .register-container {
    padding: 30px 10px;
  }

  .register-card {
    padding: 20px;
    border-radius: 12px;
    margin-top: 20px;
  }

  .register-form {
    max-width: 100%;
    gap: 10px;
  }

  .form-title {
    font-size: 20px;
    margin-bottom: 15px;
  }

  .register-form input,
  input[type="date"],
  .btn {
    font-size: 14px;
    padding: 10px;
  }

  .checkbox {
    flex-direction: column;
    align-items: flex-start;
    font-size: 13px;
  }

  .checkbox span {
    font-size: 13px;
    line-height: 1.4;
  }

  .login-link {
    font-size: 13px;
    text-align: center;
  }

  .error,
  .input-error {
    font-size: 12px;
  }
}
</style>
  